'use client';

import { IconBuildingBank, IconCreditCard, IconPlus } from '@tabler/icons-react';

import { SubscriptionPaymentMethodData } from '@/backend-api/Api';
import { cn } from '@/lib/css-utils';
import { Loader } from '@/ui-lib/loader';

interface PaymentMethodSelectorProps {
    paymentMethods: SubscriptionPaymentMethodData[];
    selectedMethodId: string | null;
    onSelectMethod: (methodId: string | null) => void;
    showAddNew?: boolean;

    isLoading?: boolean;

    hasPaymentMethodError?: boolean;
}

export function PaymentMethodSelector({
    paymentMethods,
    selectedMethodId,
    onSelectMethod,
    showAddNew = true,
    isLoading,
    hasPaymentMethodError,
}: PaymentMethodSelectorProps) {
    return (
        <div className="space-y-3">
            <h3 className="text-sm font-semibold text-neutral-300 uppercase tracking-wide">Payment Method</h3>

            {isLoading && (
                <div className="flex justify-center py-4">
                    <Loader />
                </div>
            )}

            {/* Saved Payment Methods */}
            {(paymentMethods || []).map((method) => (
                <button
                    type="button"
                    key={method.id}
                    onClick={() => onSelectMethod(method.id)}
                    className={`w-full flex items-center gap-4 p-4 rounded-lg border-2 transition-all ${
                        selectedMethodId === method.id
                            ? 'border-green-500 bg-green-500/10'
                            : 'border-neutral-700 bg-neutral-900 hover:border-neutral-600'
                    }`}
                >
                    {/* Icon */}
                    <div
                        className={`w-12 h-12 rounded flex items-center justify-center ${
                            selectedMethodId === method.id ? 'bg-green-500/20' : 'bg-neutral-800'
                        }`}
                    >
                        {method.type === 'card' ? (
                            <IconCreditCard
                                className={`w-6 h-6 ${
                                    selectedMethodId === method.id ? 'text-green-400' : 'text-neutral-400'
                                }`}
                            />
                        ) : (
                            <IconBuildingBank
                                className={`w-6 h-6 ${
                                    selectedMethodId === method.id ? 'text-green-400' : 'text-neutral-400'
                                }`}
                            />
                        )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 text-left">
                        <p className="text-sm text-neutral-100 font-medium">
                            {method.type === 'card'
                                ? `${method.cardBrand} •••• ${method.cardLast4}`
                                : `${method.fallbackDisplayName}`}
                        </p>
                        <p className="text-xs text-neutral-400 mt-1">
                            {method.type === 'card' &&
                                method.cardExpMonth &&
                                method.cardExpYear &&
                                `Expires ${method.cardExpMonth}/${method.cardExpYear}`}
                            {method.isDefault && (
                                <span className="ml-2 px-2 py-0.5 bg-green-500/10 text-green-500 text-xs rounded">
                                    Default
                                </span>
                            )}
                        </p>
                    </div>

                    {/* Selection Indicator */}
                    <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            selectedMethodId === method.id ? 'border-green-500 bg-green-500' : 'border-neutral-600'
                        }`}
                    >
                        {selectedMethodId === method.id && <div className="w-2 h-2 rounded-full bg-white"></div>}
                    </div>
                </button>
            ))}

            {/* Add New Payment Method Button */}
            {showAddNew && !isLoading && (
                <button
                    type="button"
                    onClick={() => onSelectMethod(null)}
                    className={cn(
                        'w-full flex items-center gap-4 p-4 rounded-lg border-2 border-neutral-700 bg-neutral-900 hover:border-neutral-500 hover:bg-neutral-800 transition-all',
                        {
                            'border-red-500': hasPaymentMethodError,
                        }
                    )}
                    data-testid="add-new-payment-method"
                >
                    {/* Icon */}
                    <div className="w-12 h-12 rounded flex items-center justify-center bg-neutral-800">
                        <IconPlus className="w-6 h-6 text-neutral-400" />
                    </div>

                    {/* Details */}
                    <div className="flex-1 text-left">
                        <p className="text-sm text-neutral-100 font-medium">Add new payment method</p>
                        <p className="text-xs text-neutral-400 mt-1">Use a different card or bank account</p>
                    </div>
                </button>
            )}
        </div>
    );
}
